import { useState, useEffect, useRef } from "react";
import { ChevronRight, ChevronLeft, Mic, MicOff, TrendingUp, DollarSign, Award, Users, Star, Flame, Trophy, Clock, Volume2, Play, Pause, BarChart3, Target, Zap, Crown, Shield, MessageCircle, ThumbsUp, ChevronDown, Check, X, Lock, ArrowRight, Home, User, Bell, BookOpen } from "lucide-react";

// ============================================
// BESPOKE SERVER APP — THE GROVE SF PROTOTYPE
// Duolingo-inspired XP + persistent record button
// Customized for The Grove Restaurant & Bar, 690 Mission St, SF
// ============================================

const C = {
  bg: "#0A1A0F",
  bgCard: "#12261A",
  bgCardLight: "#1A3326",
  bgCardHover: "#1F3D2D",
  green: "#00D26A",
  greenLight: "#34E88A",
  greenDark: "#00A855",
  greenMuted: "rgba(0,210,106,0.15)",
  greenGlow: "rgba(0,210,106,0.3)",
  gold: "#FFD700",
  goldLight: "#FFE44D",
  goldMuted: "rgba(255,215,0,0.15)",
  goldGlow: "rgba(255,215,0,0.25)",
  white: "#FFFFFF",
  textPrimary: "#F0F7F2",
  textSecondary: "#8BA898",
  textMuted: "#5C7A68",
  red: "#FF4757",
  redMuted: "rgba(255,71,87,0.15)",
  orange: "#FF9F43",
  purple: "#A855F7",
  border: "rgba(0,210,106,0.12)",
};

const pill = (active) => ({
  padding: "6px 14px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
  background: active ? C.green : C.bgCardLight,
  color: active ? C.bg : C.textSecondary,
  border: "none",
  cursor: "pointer",
  transition: "all 0.2s",
});

const card = {
  background: C.bgCard,
  borderRadius: 16,
  padding: 16,
  border: `1px solid ${C.border}`,
};

const bigNumber = {
  fontSize: 36,
  fontWeight: 800,
  letterSpacing: "-1px",
};

// ============================================
// CSS KEYFRAMES (injected once)
// ============================================
const StyleInjector = () => {
  useEffect(() => {
    if (document.getElementById("bespoke-keyframes")) return;
    const style = document.createElement("style");
    style.id = "bespoke-keyframes";
    style.textContent = `
      @keyframes bespoke-pulse {
        0% { transform: scale(1); opacity: 0.6; }
        50% { transform: scale(1.3); opacity: 0; }
        100% { transform: scale(1); opacity: 0; }
      }
      @keyframes bespoke-pulse2 {
        0% { transform: scale(1); opacity: 0.4; }
        50% { transform: scale(1.5); opacity: 0; }
        100% { transform: scale(1); opacity: 0; }
      }
      @keyframes bespoke-glow {
        0% { box-shadow: 0 0 8px rgba(0,210,106,0.4); }
        50% { box-shadow: 0 0 20px rgba(0,210,106,0.7), 0 0 40px rgba(0,210,106,0.3); }
        100% { box-shadow: 0 0 8px rgba(0,210,106,0.4); }
      }
      @keyframes bespoke-xp-pop {
        0% { transform: scale(0.5) translateY(0); opacity: 0; }
        30% { transform: scale(1.3) translateY(-8px); opacity: 1; }
        100% { transform: scale(1) translateY(-4px); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
};

// ============================================
// XP BADGE (Duolingo-style — shown on various screens)
// ============================================
function XPBadge({ xp, level, compact = false }) {
  if (compact) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 4,
        background: C.goldMuted, borderRadius: 12, padding: "3px 10px 3px 6px",
      }}>
        <Zap size={12} color={C.gold} fill={C.gold} />
        <span style={{ fontSize: 11, fontWeight: 800, color: C.gold }}>{xp}</span>
      </div>
    );
  }
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: C.goldMuted, borderRadius: 14, padding: "6px 14px 6px 10px",
      border: `1px solid ${C.goldGlow}`,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 14,
        background: `linear-gradient(135deg, ${C.gold}, #FF9F43)`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Zap size={14} color="#FFF" fill="#FFF" />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.gold }}>{xp} XP</div>
        <div style={{ fontSize: 9, color: C.textMuted }}>Level {level}</div>
      </div>
    </div>
  );
}

// ============================================
// PRIVACY BADGE (reused on multiple screens)
// ============================================
function PrivacyBadge({ compact = false }) {
  if (compact) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        background: "rgba(0,210,106,0.08)", borderRadius: 10, padding: "6px 10px",
        border: `1px solid ${C.border}`,
      }}>
        <Shield size={12} color={C.green} />
        <span style={{ fontSize: 10, color: C.textSecondary }}>
          Only you see this. Management gets <span style={{ color: C.green, fontWeight: 600 }}>metrics only</span> — no audio, no transcripts, no feed.
        </span>
      </div>
    );
  }
  return (
    <div style={{
      ...card,
      background: C.greenMuted,
      border: `1px solid ${C.greenGlow}`,
      display: "flex", alignItems: "center", gap: 10,
      padding: 12,
    }}>
      <Shield size={18} color={C.green} />
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.green }}>Data Firewall™</div>
        <div style={{ fontSize: 11, color: C.textSecondary }}>Management sees revenue metrics only — never your audio, transcripts, or crew feed.</div>
      </div>
    </div>
  );
}

// ============================================
// IPHONE FRAME
// ============================================
function IPhoneFrame({ children, time = "9:41" }) {
  return (
    <div style={{
      width: 375, height: 812, background: C.bg, borderRadius: 44,
      overflow: "hidden", position: "relative",
      border: "3px solid #2A2A2A",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.05)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        height: 54, display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        padding: "0 28px 4px", position: "relative", zIndex: 100,
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: C.white }}>{time}</span>
        <div style={{
          position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 126, height: 34, background: "#000", borderRadius: "0 0 20px 20px",
        }} />
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <div style={{ width: 16, height: 10, display: "flex", gap: 1, alignItems: "flex-end" }}>
            {[4, 6, 8, 10].map((h, i) => (
              <div key={i} style={{ width: 3, height: h, background: C.white, borderRadius: 1 }} />
            ))}
          </div>
          <svg width="15" height="11" viewBox="0 0 15 11"><path d="M7.5 3.5C9.4 3.5 11.1 4.3 12.3 5.5L14 3.8C12.3 2.1 10 1 7.5 1S2.7 2.1 1 3.8L2.7 5.5C3.9 4.3 5.6 3.5 7.5 3.5z" fill={C.white}/><circle cx="7.5" cy="9.5" r="1.5" fill={C.white}/><path d="M7.5 5.5C8.9 5.5 10.1 6 11 6.9L12.3 5.5C11.1 4.3 9.4 3.5 7.5 3.5S3.9 4.3 2.7 5.5L4 6.9C4.9 6 6.1 5.5 7.5 5.5z" fill={C.white}/></svg>
          <div style={{ width: 25, height: 12, border: `1px solid ${C.white}`, borderRadius: 3, padding: 1, position: "relative" }}>
            <div style={{ width: "75%", height: "100%", background: C.green, borderRadius: 1.5 }} />
            <div style={{ position: "absolute", right: -3, top: 3, width: 2, height: 5, background: C.white, borderRadius: "0 1px 1px 0" }} />
          </div>
        </div>
      </div>
      <div style={{ height: 812 - 54 - 34, overflow: "hidden", position: "relative" }}>
        {children}
      </div>
      <div style={{ height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 134, height: 5, background: "rgba(255,255,255,0.3)", borderRadius: 3 }} />
      </div>
    </div>
  );
}

// ============================================
// BOTTOM TAB BAR WITH CENTER RECORD BUTTON
// ============================================
function TabBar({ active, onNav, isRecording, onToggleRecord }) {
  const tabs = [
    { id: "onboarding", icon: BookOpen, label: "Learn" },
    { id: "home", icon: DollarSign, label: "Earnings" },
    { id: "record", icon: Mic, label: "Record", isCenter: true },
    { id: "crew", icon: Users, label: "Crew" },
    { id: "profile", icon: User, label: "Profile" },
  ];
  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0, height: 78,
      background: "linear-gradient(to top, rgba(10,26,15,0.98), rgba(10,26,15,0.9))",
      backdropFilter: "blur(20px)",
      display: "flex", justifyContent: "space-around", alignItems: "flex-end",
      paddingBottom: 6, borderTop: `1px solid ${C.border}`,
    }}>
      {tabs.map((t) => {
        if (t.isCenter) {
          // Center record button — always visible, animated when recording
          return (
            <button key="record" onClick={onToggleRecord} style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              padding: 0, marginTop: -22, position: "relative",
            }}>
              {/* Pulse rings when recording */}
              {isRecording && (
                <>
                  <div style={{
                    position: "absolute", top: -4, left: "50%", transform: "translateX(-50%)",
                    width: 64, height: 64, borderRadius: 32,
                    border: `2px solid ${C.green}`,
                    animation: "bespoke-pulse 1.5s ease-in-out infinite",
                  }} />
                  <div style={{
                    position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
                    width: 72, height: 72, borderRadius: 36,
                    border: `1px solid ${C.green}`,
                    animation: "bespoke-pulse2 1.5s ease-in-out infinite 0.3s",
                  }} />
                </>
              )}
              <div style={{
                width: 56, height: 56, borderRadius: 28,
                background: isRecording
                  ? `linear-gradient(135deg, ${C.green}, ${C.greenDark})`
                  : C.bgCardLight,
                border: `3px solid ${isRecording ? C.greenLight : C.textMuted}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                animation: isRecording ? "bespoke-glow 2s ease-in-out infinite" : "none",
                transition: "all 0.3s",
              }}>
                {isRecording
                  ? <Mic size={24} color={C.bg} strokeWidth={2.5} />
                  : <Mic size={24} color={C.textMuted} />
                }
              </div>
              <span style={{
                fontSize: 9, fontWeight: 700, marginTop: 1,
                color: isRecording ? C.green : C.textMuted,
              }}>
                {isRecording ? "● LIVE" : "Record"}
              </span>
            </button>
          );
        }
        const isActive = active === t.id;
        const Icon = t.icon;
        return (
          <button key={t.id} onClick={() => onNav(t.id)} style={{
            background: "none", border: "none", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 3, cursor: "pointer", padding: "6px 12px",
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              background: isActive ? C.greenMuted : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}>
              <Icon size={18} color={isActive ? C.green : C.textMuted} strokeWidth={isActive ? 2.5 : 1.8} />
            </div>
            <span style={{ fontSize: 9, fontWeight: isActive ? 700 : 500, color: isActive ? C.green : C.textMuted }}>
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================
// SCREEN: HOME / EARNINGS (The Money Screen)
// ============================================
function HomeScreen({ isRecording }) {
  const [period, setPeriod] = useState("today");
  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 90 }}>
      {/* Header with XP */}
      <div style={{ padding: "8px 20px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, color: C.textSecondary }}>Hey Tara 👋</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginTop: 2 }}>Your Money</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <XPBadge xp={1240} level={8} compact />
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              background: C.bgCard, border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              position: "relative",
            }}>
              <Bell size={16} color={C.textSecondary} />
              <div style={{
                position: "absolute", top: -2, right: -2,
                width: 8, height: 8, borderRadius: 4,
                background: C.red, border: `2px solid ${C.bg}`,
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Recording banner if active */}
      {isRecording && (
        <div style={{
          margin: "10px 20px 0", padding: "8px 12px", borderRadius: 10,
          background: C.greenMuted, border: `1px solid ${C.greenGlow}`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4, background: C.green,
            animation: "bespoke-glow 1.5s ease-in-out infinite",
          }} />
          <span style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>Shift recording in progress</span>
          <span style={{ fontSize: 11, color: C.textMuted, marginLeft: "auto" }}>Tap mic to pause</span>
        </div>
      )}

      {/* Period selector */}
      <div style={{ display: "flex", gap: 8, padding: "12px 20px" }}>
        {["today", "week", "month"].map((p) => (
          <button key={p} onClick={() => setPeriod(p)} style={pill(period === p)}>
            {p === "today" ? "Today" : p === "week" ? "This Week" : "This Month"}
          </button>
        ))}
      </div>

      {/* Big money card */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{
          ...card,
          background: `linear-gradient(135deg, ${C.bgCard}, ${C.bgCardLight})`,
          border: `1px solid ${C.greenGlow}`,
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: -40, right: -40,
            width: 120, height: 120,
            background: `radial-gradient(circle, ${C.greenGlow}, transparent)`,
            borderRadius: "50%",
          }} />
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 12, color: C.textSecondary, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
              {period === "today" ? "Estimated tip lift today" : period === "week" ? "Tip lift this week" : "Tip lift this month"}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 6 }}>
              <span style={{ ...bigNumber, color: C.green }}>
                {period === "today" ? "+$24" : period === "week" ? "+$147" : "+$583"}
              </span>
              <span style={{ fontSize: 14, color: C.greenLight, fontWeight: 600 }}>
                {period === "today" ? "↑ 12% vs avg" : period === "week" ? "↑ 8% vs last wk" : "↑ 15% vs last mo"}
              </span>
            </div>
            {period === "month" && (
              <div style={{ fontSize: 13, color: C.gold, fontWeight: 600, marginTop: 8 }}>
                On pace for +$6,996 this year
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Today's shift card */}
      {period === "today" && (
        <div style={{ padding: "0 20px", marginBottom: 16 }}>
          <div style={{ ...card }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>Today's Shift</div>
              <div style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}>11:00 AM — 4:00 PM</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {[
                { label: "Orders", value: "14", sub: "of 18 avg" },
                { label: "Upsells", value: "10/14", sub: "71%" },
                { label: "Avg Check", value: "$42", sub: "↑ $6" },
              ].map((s, i) => (
                <div key={i} style={{
                  background: C.bgCardLight, borderRadius: 12, padding: "10px 8px", textAlign: "center",
                }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.textPrimary }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: C.green, marginTop: 1 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Duolingo-style Daily XP progress */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ ...card, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Zap size={16} color={C.gold} fill={C.gold} />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>Daily XP</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>85 / 100</span>
          </div>
          <div style={{ background: C.bgCardLight, borderRadius: 6, height: 10, overflow: "hidden" }}>
            <div style={{ width: "85%", height: "100%", background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`, borderRadius: 6 }} />
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
            15 XP to hit your daily goal! Complete a drill or finish your shift.
          </div>
        </div>
      </div>

      {/* Streak */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{
          ...card, display: "flex", alignItems: "center", gap: 14, background: C.bgCard,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: `linear-gradient(135deg, #FF6B35, ${C.gold})`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Flame size={26} color="#FFF" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary }}>7-day streak 🔥</div>
            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>You've used Bespoke every shift this week!</div>
          </div>
        </div>
      </div>

      {/* Quick insights */}
      <div style={{ padding: "0 20px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>What's Working</div>
        {[
          { icon: "🍹", text: "Cocktail upsells are your money move", detail: "+$9 avg when you suggest a drink at the counter", color: C.green },
          { icon: "🍔", text: "Burger add-on rate climbing", detail: "Bacon/avocado adds up — 44% → 61% this month", color: C.gold },
          { icon: "💡", text: "Try today", detail: "The Grove Burger has the highest margin — lead with it at the counter", color: C.orange },
        ].map((tip, i) => (
          <div key={i} style={{
            ...card, display: "flex", alignItems: "center", gap: 12, marginBottom: 8, padding: 14,
          }}>
            <div style={{ fontSize: 24 }}>{tip.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{tip.text}</div>
              <div style={{ fontSize: 12, color: tip.color, marginTop: 2 }}>{tip.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// SCREEN: SHIFT (Pre / Active / Post) — now accessed via center button
// ============================================
function ShiftScreen({ isRecording, seconds, onEnd }) {
  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  if (!isRecording && seconds === 0) {
    // Pre-shift prep
    return (
      <div style={{ height: "100%", overflowY: "auto", paddingBottom: 90 }}>
        <div style={{ padding: "8px 20px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>Pre-Shift Prep</div>
              <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 2 }}>Tuesday, March 25 • Lunch at The Grove</div>
            </div>
            <XPBadge xp={1240} level={8} compact />
          </div>
        </div>

        {/* Today's Playbook */}
        <div style={{ padding: "16px 20px" }}>
          <div style={{
            ...card,
            background: `linear-gradient(135deg, ${C.bgCard}, rgba(255,215,0,0.06))`,
            border: `1px solid ${C.goldGlow}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Target size={18} color={C.gold} />
              <span style={{ fontSize: 15, fontWeight: 700, color: C.gold }}>Today's Playbook</span>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Push These</div>
              {[
                { dish: "The Grove Burger", price: "$19", margin: "74%", pair: "Craft cocktail or draft beer" },
                { dish: "Slow-Cooked Hot Pastrami", price: "$18", margin: "68%", pair: "House-made lemonade or wine" },
              ].map((item, i) => (
                <div key={i} style={{
                  background: C.bgCardLight, borderRadius: 10, padding: 12, marginBottom: 6,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{item.dish}</div>
                    <div style={{ fontSize: 11, color: C.textSecondary, marginTop: 2 }}>Pair with {item.pair}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{item.price}</div>
                    <div style={{ fontSize: 10, color: C.gold }}>{item.margin} margin</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{
              background: C.greenMuted, borderRadius: 10, padding: 12,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <Flame size={20} color={C.green} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.green }}>You're on a 6-order drink upsell streak</div>
                <div style={{ fontSize: 11, color: C.textSecondary }}>Keep suggesting cocktails at the counter</div>
              </div>
            </div>
          </div>
        </div>

        {/* 60-Sec Drill with XP reward */}
        <div style={{ padding: "0 20px", marginBottom: 16 }}>
          <div style={{ ...card }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Volume2 size={18} color={C.green} />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>60-Sec Drill</span>
              <div style={{
                marginLeft: "auto",
                display: "flex", alignItems: "center", gap: 4,
                background: C.goldMuted, borderRadius: 8, padding: "3px 8px",
              }}>
                <Zap size={10} color={C.gold} fill={C.gold} />
                <span style={{ fontSize: 10, fontWeight: 800, color: C.gold }}>+25 XP</span>
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
              "A guest just ordered the Grove Burger. Suggest a drink pairing and a side upgrade."
            </div>
            <div style={{
              background: C.bgCardLight, borderRadius: 10, padding: 10, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <Star size={14} color={C.gold} fill={C.gold} />
              <span style={{ fontSize: 11, color: C.textSecondary }}>
                Score 80%+ to earn <span style={{ color: C.gold, fontWeight: 700 }}>bonus 15 XP</span>. Top score: <span style={{ color: C.green, fontWeight: 700 }}>92%</span>
              </span>
            </div>
            <button style={{
              width: "100%", padding: "12px", borderRadius: 12,
              background: `linear-gradient(135deg, ${C.green}, ${C.greenDark})`,
              border: "none", color: C.bg, fontSize: 14, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: `0 2px 12px ${C.greenGlow}`,
            }}>
              <Mic size={16} /> Practice Now — Earn XP
            </button>
          </div>
        </div>

        {/* Start shift CTA */}
        <div style={{ padding: "0 20px", marginBottom: 16 }}>
          <div style={{
            ...card, padding: 14, textAlign: "center",
            background: C.bgCardLight, border: `1px dashed ${C.textMuted}`,
          }}>
            <Mic size={24} color={C.textMuted} style={{ margin: "0 auto 8px" }} />
            <div style={{ fontSize: 13, color: C.textSecondary }}>
              Tap the <span style={{ color: C.green, fontWeight: 700 }}>mic button</span> below to start your shift recording
            </div>
          </div>
        </div>

        <div style={{ padding: "0 20px" }}>
          <PrivacyBadge compact />
        </div>
      </div>
    );
  }

  if (isRecording || (seconds > 0 && seconds < 999)) {
    // Active shift view
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingBottom: 90 }}>
        <div style={{
          width: 140, height: 140, borderRadius: 70,
          background: isRecording ? `radial-gradient(circle, ${C.greenMuted}, transparent)` : C.bgCard,
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", marginBottom: 24,
        }}>
          {isRecording && (
            <>
              <div style={{
                position: "absolute", width: 170, height: 170, borderRadius: 85,
                border: `2px solid ${C.green}`,
                animation: "bespoke-pulse 1.5s ease-in-out infinite",
              }} />
              <div style={{
                position: "absolute", width: 200, height: 200, borderRadius: 100,
                border: `1px solid ${C.green}`,
                animation: "bespoke-pulse2 1.5s ease-in-out infinite 0.3s",
              }} />
            </>
          )}
          <div style={{
            width: 100, height: 100, borderRadius: 50,
            background: isRecording ? `linear-gradient(135deg, ${C.green}, ${C.greenDark})` : C.bgCardLight,
            border: `3px solid ${isRecording ? C.greenLight : C.textMuted}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: isRecording ? "bespoke-glow 2s ease-in-out infinite" : "none",
          }}>
            {isRecording ? <Mic size={36} color={C.bg} /> : <MicOff size={36} color={C.textMuted} />}
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: isRecording ? C.green : C.textMuted, textTransform: "uppercase", letterSpacing: 2 }}>
            {isRecording ? "● Recording" : "Paused"}
          </div>
          <div style={{ fontSize: 44, fontWeight: 800, color: C.textPrimary, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
            {formatTime(seconds)}
          </div>
        </div>

        {/* Live stats */}
        <div style={{ display: "flex", gap: 20, marginTop: 24 }}>
          {[
            { label: "Orders", value: "14" },
            { label: "Upsells", value: "10" },
            { label: "Est. Lift", value: "+$22" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: i === 2 ? C.green : C.textPrimary }}>{s.value}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* XP earned this shift */}
        <div style={{
          marginTop: 20, display: "flex", alignItems: "center", gap: 8,
          background: C.goldMuted, borderRadius: 12, padding: "8px 16px",
          border: `1px solid ${C.goldGlow}`,
        }}>
          <Zap size={16} color={C.gold} fill={C.gold} />
          <span style={{ fontSize: 13, fontWeight: 700, color: C.gold }}>+60 XP earned this shift</span>
        </div>

        {/* Privacy note */}
        <div style={{ marginTop: 16, padding: "0 40px" }}>
          <PrivacyBadge compact />
        </div>

        <button onClick={onEnd} style={{
          marginTop: 20, padding: "10px 28px", borderRadius: 12,
          background: C.bgCard, border: `1px solid ${C.red}`,
          color: C.red, fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>
          End Shift
        </button>
      </div>
    );
  }

  // Post-shift scorecard
  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 90 }}>
      <div style={{ padding: "8px 20px 0" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>Shift Complete 🎉</div>
        <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 2 }}>Tuesday lunch • 11:04 AM — 3:52 PM</div>
      </div>

      {/* XP earned banner */}
      <div style={{ padding: "12px 20px" }}>
        <div style={{
          background: `linear-gradient(135deg, ${C.goldMuted}, rgba(255,159,67,0.1))`,
          border: `1px solid ${C.goldGlow}`,
          borderRadius: 14, padding: 16, textAlign: "center",
        }}>
          <div style={{ fontSize: 12, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>XP Earned This Shift</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: C.gold, marginTop: 4 }}>+85 XP</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 8 }}>
            {[
              { label: "Shift bonus", val: "+40" },
              { label: "Upsell streak", val: "+20" },
              { label: "Drill score", val: "+25" },
            ].map((x, i) => (
              <div key={i} style={{ fontSize: 10, color: C.textSecondary }}>
                {x.label}: <span style={{ color: C.gold, fontWeight: 700 }}>{x.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The big tip lift number */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{
          ...card,
          background: `linear-gradient(135deg, ${C.bgCard}, rgba(0,210,106,0.08))`,
          border: `1px solid ${C.greenGlow}`,
          textAlign: "center", padding: 20,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, textTransform: "uppercase", letterSpacing: 1 }}>
            Estimated Tip Lift Today
          </div>
          <div style={{ fontSize: 52, fontWeight: 900, color: C.green, marginTop: 4, letterSpacing: -2 }}>+$27</div>
          <div style={{ fontSize: 14, color: C.greenLight, marginTop: 4 }}>Your best Tuesday lunch in 3 weeks</div>
        </div>
      </div>

      {/* Scorecard grid */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "Orders Served", value: "22", icon: "🍽️" },
            { label: "Drink Upsell Rate", value: "68%", icon: "📈", highlight: true },
            { label: "Revenue Generated", value: "$847", icon: "💰" },
            { label: "Avg Check Lift", value: "+$6", icon: "⬆️", highlight: true },
          ].map((s, i) => (
            <div key={i} style={{
              ...card, textAlign: "center", padding: 14,
              border: s.highlight ? `1px solid ${C.greenGlow}` : `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.highlight ? C.green : C.textPrimary }}>{s.value}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* What worked */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ ...card }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 12 }}>What Worked Today</div>
          {[
            { text: "Cocktail upsell on 15 of 22 orders", tag: "🔥 Career high", color: C.gold },
            { text: "Burger add-on on 8 orders", tag: "↑ 22%", color: C.green },
            { text: "Mentioned Grove Cookie on every check", tag: "Perfect", color: C.green },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 0", borderBottom: i < 2 ? `1px solid ${C.border}` : "none",
            }}>
              <span style={{ fontSize: 13, color: C.textPrimary }}>{item.text}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: item.color }}>{item.tag}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Coach tip */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ ...card, background: C.goldMuted, border: `1px solid ${C.goldGlow}` }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ fontSize: 22 }}>💡</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.gold }}>Coach's Note</div>
              <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
                You crushed drink upsells today. Next level: when someone orders a sandwich, suggest the fresh-baked cookie before they leave the counter. Servers who do this add +$4 per check.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 20px" }}>
        <PrivacyBadge compact />
      </div>
    </div>
  );
}

// ============================================
// SCREEN: CREW (Social / Gamification)
// ============================================
function CrewScreen({ isRecording }) {
  const [tab, setTab] = useState("chat");
  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 90 }}>
      <div style={{ padding: "8px 20px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>The Crew</div>
            <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 2 }}>The Grove • Yerba Buena</div>
          </div>
          <XPBadge xp={1240} level={8} compact />
        </div>
      </div>

      {isRecording && (
        <div style={{
          margin: "10px 20px 0", padding: "8px 12px", borderRadius: 10,
          background: C.greenMuted, border: `1px solid ${C.greenGlow}`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4, background: C.green,
            animation: "bespoke-glow 1.5s ease-in-out infinite",
          }} />
          <span style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>Shift recording in progress</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, padding: "12px 20px" }}>
        {["chat", "battles", "challenges"].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={pill(tab === t)}>
            {t === "chat" ? "Feed" : t === "battles" ? "Battles" : "Challenges"}
          </button>
        ))}
      </div>

      {tab === "chat" && (
        <div style={{ padding: "0 20px" }}>
          {/* Privacy note at top of feed */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 10px", marginBottom: 10, borderRadius: 8,
            background: "rgba(0,210,106,0.05)",
          }}>
            <Lock size={11} color={C.textMuted} />
            <span style={{ fontSize: 10, color: C.textMuted }}>
              This feed is <span style={{ color: C.green, fontWeight: 600 }}>crew-only</span>. Management cannot see it.
            </span>
          </div>
          {[
            { name: "Mia", avatar: "🟣", time: "1:42 PM", text: "Just hit 20-order drink upsell streak 🔥🔥🔥 counter flow is insane today", likes: 6, xp: "+15 XP" },
            { name: "Dev", avatar: "🔵", time: "1:15 PM", text: "Moved 6 Grove Burgers with bacon add-on this shift. That counter pitch actually works lol", likes: 3, xp: "+10 XP" },
            { name: "You", avatar: "🟢", time: "12:50 PM", text: "Cocktail upsell career high — 15 out of 22 orders today", likes: 8, isYou: true, xp: "+20 XP" },
            { name: "Sam", avatar: "🟡", time: "12:20 PM", text: "Guy at counter tipped $12 on a $19 sandwich after I described the pastrami. This app is paying my rent 😂", likes: 12, xp: "+10 XP" },
            { name: "Mia", avatar: "🟣", time: "11:45 AM", text: "Monday crew set the bar at $1,800 revenue. We gotta beat that today 💪", likes: 4 },
          ].map((msg, i) => (
            <div key={i} style={{
              ...card, marginBottom: 8, padding: 14,
              border: msg.isYou ? `1px solid ${C.greenGlow}` : `1px solid ${C.border}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{msg.avatar}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: msg.isYou ? C.green : C.textPrimary }}>{msg.name}</span>
                  {msg.xp && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: C.gold, background: C.goldMuted, padding: "2px 6px", borderRadius: 6 }}>{msg.xp}</span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: C.textMuted }}>{msg.time}</span>
              </div>
              <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.5, marginLeft: 24 }}>{msg.text}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, marginLeft: 24 }}>
                <ThumbsUp size={13} color={C.textMuted} />
                <span style={{ fontSize: 11, color: C.textMuted }}>{msg.likes}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "battles" && (
        <div style={{ padding: "0 20px" }}>
          <div style={{
            ...card,
            background: `linear-gradient(135deg, ${C.bgCard}, rgba(255,215,0,0.06))`,
            border: `1px solid ${C.goldGlow}`, marginBottom: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Trophy size={18} color={C.gold} />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.gold }}>Active Battle</span>
              <span style={{ fontSize: 11, color: C.textMuted, marginLeft: "auto" }}>Ends Sunday</span>
            </div>
            <div style={{ textAlign: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 6 }}>Lunch Crew vs Dinner Crew</div>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: C.green }}>$1,847</div>
                  <div style={{ fontSize: 11, color: C.green }}>Lunch (You)</div>
                </div>
                <div style={{ fontSize: 14, color: C.textMuted, fontWeight: 700 }}>vs</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: C.textSecondary }}>$1,620</div>
                  <div style={{ fontSize: 11, color: C.textSecondary }}>Dinner</div>
                </div>
              </div>
            </div>
            <div style={{ background: C.bgCardLight, borderRadius: 6, height: 10, overflow: "hidden" }}>
              <div style={{ width: "54%", height: "100%", background: `linear-gradient(90deg, ${C.green}, ${C.greenLight})`, borderRadius: 6 }} />
            </div>
            <div style={{ fontSize: 11, color: C.green, fontWeight: 600, marginTop: 6, textAlign: "center" }}>
              Leading by $227 🔥 • Winners get +100 XP
            </div>
          </div>
          <div style={{ ...card, display: "flex", alignItems: "center", gap: 12 }}>
            <Crown size={22} color={C.gold} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>Winner Gets</div>
              <div style={{ fontSize: 12, color: C.gold }}>Choice of station + free meal + 100 XP</div>
            </div>
          </div>
        </div>
      )}

      {tab === "challenges" && (
        <div style={{ padding: "0 20px" }}>
          {[
            { name: "The Bartender", desc: "Upsell a cocktail or beer on 5 counter orders", progress: 4, total: 5, reward: "🍹 Badge + First Cut + 50 XP", color: C.purple, hot: true },
            { name: "Cookie Monster", desc: "Sell 15 fresh-baked cookies this week", progress: 11, total: 15, reward: "🍪 Badge + Free meal + 40 XP", color: C.gold },
            { name: "The Grove Special", desc: "Move 10 Grove Burgers with add-ons in a week", progress: 6, total: 10, reward: "🍔 Badge + Station pick + 60 XP", color: C.green },
          ].map((ch, i) => (
            <div key={i} style={{
              ...card, marginBottom: 10,
              border: ch.hot ? `1px solid rgba(168,85,247,0.3)` : `1px solid ${C.border}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{ch.name}</div>
                {ch.hot && <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, background: "rgba(168,85,247,0.15)", padding: "3px 8px", borderRadius: 8 }}>ALMOST</span>}
              </div>
              <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 10 }}>{ch.desc}</div>
              <div style={{ background: C.bgCardLight, borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ width: `${(ch.progress / ch.total) * 100}%`, height: "100%", background: ch.color, borderRadius: 6 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: ch.color }}>{ch.progress}/{ch.total}</span>
                <span style={{ fontSize: 11, color: C.textMuted }}>{ch.reward}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// SCREEN: PROFILE (Reputation / Career)
// ============================================
function ProfileScreen({ isRecording }) {
  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 90 }}>
      <div style={{ padding: "8px 20px 20px", textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: 36,
          background: `linear-gradient(135deg, ${C.green}, ${C.gold})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 12px", fontSize: 32,
        }}>
          T
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.textPrimary }}>Tara K.</div>
        <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 2 }}>Server • The Grove SF</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>2 years at The Grove • Counter + Table</div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
          <XPBadge xp={1240} level={8} />
        </div>
      </div>

      {isRecording && (
        <div style={{
          margin: "0 20px 12px", padding: "8px 12px", borderRadius: 10,
          background: C.greenMuted, border: `1px solid ${C.greenGlow}`,
          display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4, background: C.green,
            animation: "bespoke-glow 1.5s ease-in-out infinite",
          }} />
          <span style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>Recording in progress</span>
        </div>
      )}

      {/* Duolingo-style level progress */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ ...card, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>Level 8 → Level 9</span>
            <span style={{ fontSize: 11, color: C.textMuted }}>1240 / 1500 XP</span>
          </div>
          <div style={{ background: C.bgCardLight, borderRadius: 6, height: 10, overflow: "hidden" }}>
            <div style={{ width: "83%", height: "100%", background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`, borderRadius: 6 }} />
          </div>
          <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>260 XP to next level. You earn XP from shifts, drills, challenges, and streaks.</div>
        </div>
      </div>

      {/* Bespoke Score */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{
          ...card,
          background: `linear-gradient(135deg, ${C.bgCard}, rgba(0,210,106,0.08))`,
          border: `1px solid ${C.greenGlow}`, textAlign: "center", padding: 20,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, textTransform: "uppercase", letterSpacing: 1 }}>
            Bespoke Score
          </div>
          <div style={{
            width: 90, height: 90, borderRadius: 45,
            border: `4px solid ${C.green}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "12px auto",
          }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: C.green }}>87</div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
            <span style={{ fontSize: 12, color: C.gold, fontWeight: 700 }}>Top 15%</span>
            <span style={{ fontSize: 12, color: C.textMuted }}>of all servers</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 16 }}>
            {[
              { label: "Upsell", score: 92 },
              { label: "Hospitality", score: 84 },
              { label: "Consistency", score: 88 },
              { label: "Peer Rep", score: 82 },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: s.score >= 90 ? C.green : C.textPrimary }}>{s.score}</div>
                <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Career stats */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>Career Portfolio</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "Revenue Attributed", value: "$42.8K", icon: "💰" },
            { label: "Shifts Logged", value: "186", icon: "📋" },
            { label: "Upsell Career Avg", value: "71%", icon: "📈" },
            { label: "Tip Lift Total", value: "+$4,290", icon: "🤑" },
          ].map((s, i) => (
            <div key={i} style={{ ...card, textAlign: "center", padding: 14 }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.textPrimary }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Badges */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>Badges</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { emoji: "🍹", name: "Drink Expert", tier: "Gold" },
            { emoji: "🔥", name: "Streak Master", tier: "Silver" },
            { emoji: "🍪", name: "Cookie Monster", tier: "Gold" },
            { emoji: "⚡", name: "Counter Pro", tier: "Gold" },
            { emoji: "🍔", name: "Burger Boss", tier: "Bronze" },
            { emoji: "🤝", name: "Team Player", tier: "Silver" },
          ].map((b, i) => (
            <div key={i} style={{
              ...card, padding: "10px 12px", textAlign: "center", width: 90,
              border: b.tier === "Gold" ? `1px solid ${C.goldGlow}` : `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>{b.emoji}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textPrimary }}>{b.name}</div>
              <div style={{ fontSize: 9, color: b.tier === "Gold" ? C.gold : b.tier === "Silver" ? "#C0C0C0" : "#CD7F32", marginTop: 2 }}>{b.tier}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Specialties */}
      <div style={{ padding: "0 20px", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>Specialties</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["Counter Upselling", "Drink Pairings", "Comfort Food Expert", "Lunch Rush Pro", "Add-On Specialist"].map((s, i) => (
            <span key={i} style={{
              padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: C.bgCardLight, color: C.textSecondary, border: `1px solid ${C.border}`,
            }}>
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Privacy note at bottom of profile */}
      <div style={{ padding: "0 20px" }}>
        <PrivacyBadge />
      </div>
    </div>
  );
}

// ============================================
// SCREEN: ONBOARDING (Duolingo-style)
// ============================================
function OnboardingScreen({ onDone }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      content: (
        <div style={{ textAlign: "center", padding: "50px 30px 30px" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>💰</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: C.textPrimary, lineHeight: 1.2 }}>
            Make more money.<br />Every single shift.
          </div>
          <div style={{ fontSize: 14, color: C.textSecondary, marginTop: 14, lineHeight: 1.6 }}>
            Bespoke learns what makes you great and helps you do more of it. Average server sees <span style={{ color: C.green, fontWeight: 700 }}>+$400/month</span> in additional tips.
          </div>
          {/* Duolingo-style XP preview */}
          <div style={{
            marginTop: 20, ...card, padding: 14,
            background: C.goldMuted, border: `1px solid ${C.goldGlow}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Zap size={18} color={C.gold} fill={C.gold} />
              <span style={{ fontSize: 14, fontWeight: 800, color: C.gold }}>Earn XP every shift</span>
            </div>
            <div style={{ fontSize: 11, color: C.textSecondary, marginTop: 6 }}>
              Level up, unlock badges, compete with your crew, and earn real rewards.
            </div>
          </div>
        </div>
      ),
    },
    {
      content: (
        <div style={{ textAlign: "center", padding: "50px 30px 30px" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎙️</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: C.textPrimary, lineHeight: 1.2 }}>
            How it works
          </div>
          <div style={{ marginTop: 20 }}>
            {[
              { num: "1", text: "Phone records during your shift (sits in your apron)", xp: "+40 XP per shift" },
              { num: "2", text: "AI scores your upselling, pairings, and guest interactions", xp: "" },
              { num: "3", text: "You get a scorecard showing how to earn more", xp: "+25 XP per drill" },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", textAlign: "left", marginBottom: 16 }}>
                <div style={{
                  minWidth: 30, height: 30, borderRadius: 10,
                  background: C.greenMuted,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 800, color: C.green,
                }}>
                  {s.num}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.5 }}>{s.text}</div>
                  {s.xp && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, background: C.goldMuted, borderRadius: 6, padding: "2px 8px" }}>
                      <Zap size={10} color={C.gold} fill={C.gold} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.gold }}>{s.xp}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      content: (
        <div style={{ textAlign: "center", padding: "50px 30px 30px" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: C.textPrimary, lineHeight: 1.2 }}>
            Your audio is yours.
          </div>
          <div style={{ fontSize: 14, color: C.textSecondary, marginTop: 14, lineHeight: 1.6 }}>
            Management <span style={{ color: C.red, fontWeight: 700 }}>never</span> hears your recordings. We process the audio, pull out the stats, then <span style={{ color: C.green, fontWeight: 700 }}>delete the recording.</span>
          </div>
          <div style={{
            marginTop: 20, ...card, background: C.greenMuted, border: `1px solid ${C.greenGlow}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
              <Shield size={20} color={C.green} />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.green }}>Data Firewall™</span>
            </div>
            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 8, lineHeight: 1.5, textAlign: "left" }}>
              Only you can replay your audio. Management sees revenue metrics only — <span style={{ color: C.green, fontWeight: 600 }}>never</span> your recordings, transcripts, or crew feed.
            </div>
          </div>
          {/* Explicit breakdown */}
          <div style={{ marginTop: 14, textAlign: "left" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, marginBottom: 8 }}>What management sees vs. what they don't:</div>
            {[
              { label: "Revenue per server", sees: true },
              { label: "Upsell % and team trends", sees: true },
              { label: "Audio recordings", sees: false },
              { label: "Transcripts of conversations", sees: false },
              { label: "The Crew feed / team chat", sees: false },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                {item.sees
                  ? <Check size={14} color={C.green} />
                  : <X size={14} color={C.red} />
                }
                <span style={{ fontSize: 12, color: item.sees ? C.textSecondary : C.red }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      content: (
        <div style={{ textAlign: "center", padding: "50px 30px 30px" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🚀</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: C.textPrimary, lineHeight: 1.2 }}>
            Let's practice!
          </div>
          <div style={{ fontSize: 14, color: C.textSecondary, marginTop: 14, lineHeight: 1.6 }}>
            Do a quick 60-second drill and earn your first XP.
          </div>
          <div style={{
            marginTop: 20, ...card, padding: 20,
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: 30,
              background: C.greenMuted, border: `2px solid ${C.green}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <Mic size={28} color={C.green} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>Practice Drill</div>
            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 6 }}>
              "A guest at the counter just asked what's good. Recommend the Grove Burger with an add-on and a drink."
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10,
              background: C.goldMuted, borderRadius: 10, padding: "6px 14px",
              border: `1px solid ${C.goldGlow}`,
            }}>
              <Zap size={14} color={C.gold} fill={C.gold} />
              <span style={{ fontSize: 12, fontWeight: 800, color: C.gold }}>Earn +25 XP</span>
              <span style={{ fontSize: 10, color: C.textMuted }}>• Score 80%+ for bonus +15 XP</span>
            </div>
            <button style={{
              marginTop: 14, padding: "10px 24px", borderRadius: 10,
              background: `linear-gradient(135deg, ${C.green}, ${C.greenDark})`,
              border: "none", color: C.bg, fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: `0 2px 12px ${C.greenGlow}`,
            }}>
              Start Drill
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {steps[step].content}
      </div>
      <div style={{ padding: "0 20px 20px" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 16 }}>
          {steps.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 24 : 8, height: 8, borderRadius: 4,
              background: i === step ? C.green : i < step ? C.greenDark : C.bgCardLight,
              transition: "all 0.3s",
            }} />
          ))}
        </div>
        <button onClick={() => {
          if (step < steps.length - 1) setStep(step + 1);
          else onDone();
        }} style={{
          width: "100%", padding: 16, borderRadius: 16,
          background: step === steps.length - 1
            ? `linear-gradient(135deg, ${C.green}, ${C.greenDark})`
            : C.green,
          border: "none", color: C.bg, fontSize: 16, fontWeight: 800, cursor: "pointer",
          boxShadow: `0 4px 24px ${C.greenGlow}`,
        }}>
          {step === steps.length - 1 ? "Let's Go 🔥" : "Continue"}
        </button>
        {step < steps.length - 1 && (
          <button onClick={() => onDone()} style={{
            width: "100%", padding: 12, marginTop: 8,
            background: "none", border: "none", color: C.textMuted, fontSize: 13, cursor: "pointer",
          }}>
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
export default function BespokeApp() {
  const [screen, setScreen] = useState("onboarding");
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [showShiftOverlay, setShowShiftOverlay] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRecording) {
      intervalRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRecording]);

  const handleToggleRecord = () => {
    if (!isRecording && seconds === 0) {
      // Start recording — show shift view
      setIsRecording(true);
      setShowShiftOverlay(true);
      setScreen("shift");
    } else if (isRecording) {
      // Pause
      setIsRecording(false);
    } else {
      // Resume
      setIsRecording(true);
    }
  };

  const handleEndShift = () => {
    setIsRecording(false);
    setSeconds(999); // trigger post-shift view
    setShowShiftOverlay(false);
  };

  const renderScreen = () => {
    if (screen === "shift" || showShiftOverlay) {
      return <ShiftScreen isRecording={isRecording} seconds={seconds} onEnd={handleEndShift} />;
    }
    switch (screen) {
      case "onboarding": return <OnboardingScreen onDone={() => setScreen("home")} />;
      case "home": return <HomeScreen isRecording={isRecording} />;
      case "crew": return <CrewScreen isRecording={isRecording} />;
      case "profile": return <ProfileScreen isRecording={isRecording} />;
      default: return <HomeScreen isRecording={isRecording} />;
    }
  };

  const showTabBar = screen !== "onboarding" || isRecording;

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "#050505", padding: 20, gap: 16,
    }}>
      <StyleInjector />
      <div style={{ textAlign: "center", fontFamily: "-apple-system, sans-serif" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#888", letterSpacing: 2, textTransform: "uppercase" }}>
          Bespoke × The Grove SF
        </div>
        <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>
          Server App Prototype • 690 Mission St • March 2026
        </div>
      </div>

      <IPhoneFrame time={isRecording ? "12:47" : "9:41"}>
        <div style={{ height: "100%", position: "relative" }}>
          {renderScreen()}
          {showTabBar && (
            <TabBar
              active={showShiftOverlay ? "shift" : screen}
              onNav={(id) => { setShowShiftOverlay(false); setScreen(id); }}
              isRecording={isRecording}
              onToggleRecord={handleToggleRecord}
            />
          )}
        </div>
      </IPhoneFrame>

      {/* Screen selector buttons */}
      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center",
        fontFamily: "-apple-system, sans-serif",
      }}>
        {[
          { id: "onboarding", label: "📖 Learn" },
          { id: "home", label: "💰 Earnings" },
          { id: "shift", label: "🎙️ Shift" },
          { id: "crew", label: "👥 Crew" },
          { id: "profile", label: "👤 Profile" },
        ].map((s) => (
          <button
            key={s.id}
            onClick={() => {
              if (s.id === "shift") {
                setShowShiftOverlay(true);
                setScreen("shift");
              } else {
                setShowShiftOverlay(false);
                setScreen(s.id);
              }
            }}
            style={{
              padding: "8px 16px", borderRadius: 10,
              background: (screen === s.id || (s.id === "shift" && showShiftOverlay))
                ? "#00D26A" : "#1a1a1a",
              border: "1px solid #333",
              color: (screen === s.id || (s.id === "shift" && showShiftOverlay))
                ? "#000" : "#888",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
